import {
  TTranslation,
  useDynamicOptionsBuilder,
} from "@/features/FormCard/hooks";
import { useEffect, useRef, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { CategoryOption, DEFAULT_CATEGORY_OPTIONS } from "./config";
import {
  getFilteredCategoryOptions,
  shouldAutoSelectOption,
  shouldResetCategoryValue,
} from "./utils";

/**
 * Hook to handle automatic selection of the single option
 *
 * @param options Available options
 * @param form Form instance
 * @param isCategoryDisabled If the category field is disabled
 * @param translations Optional translations for category values
 */
export function useAutoSelectSingleOption(
  options: any[],
  form: UseFormReturn<any>,
  isCategoryDisabled: boolean,
  translations?: TTranslation,
  keysAllowNullValue: string[] = [],
) {
  useEffect(() => {
    const currentCategory = form.watch("category") || "";

    if (currentCategory) return;
    if (isCategoryDisabled)
      return form.setValue("category", null, { shouldDirty: true });
    const { shouldSelect, selectedValue } = shouldAutoSelectOption(
      options,
      currentCategory,
      isCategoryDisabled,
      keysAllowNullValue,
    );

    if (shouldSelect && selectedValue) {
      form.setValue("category", selectedValue, { shouldDirty: true });
    }
  }, [options, isCategoryDisabled, form, translations]);
}

export function useCategoryFieldState(
  form: UseFormReturn<any>,
  isLoadingData: boolean,
) {
  const [isCategoryDisabled, setIsCategoryDisabled] = useState(false);

  const prevAssetClass2Ref = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isLoadingData) return;

    const assetClass2 = form.watch("c8_static_datum_name")?.asset_class_2;

    // Disable category field if no asset class selected
    if (!assetClass2) {
      setIsCategoryDisabled(true);
      return;
    }

    // Enable category for all other cases
    setIsCategoryDisabled(false);

    if (shouldResetCategoryValue(prevAssetClass2Ref.current, assetClass2)) {
      form.setValue("category", null, { shouldDirty: true });
    }

    prevAssetClass2Ref.current = assetClass2;
  }, [form.watch("c8_static_datum_name")?.asset_class_2, form, isLoadingData]);

  return { isCategoryDisabled };
}

/**
 * Hook to manage category auto selection
 */
export function useCategoryAutoSelection({
  form,
  translations,
  keysAllowNullValue = [],
  isLoadingData = false,
}: {
  form: UseFormReturn<FieldValues>;
  translations?: TTranslation;
  keysAllowNullValue: string[];
  isLoadingData: boolean;
}) {
  const { isCategoryDisabled } = useCategoryFieldState(form, isLoadingData);
  const assetClass2 = form.watch("c8_static_datum_name")?.asset_class_2;

  const category_options = useDynamicOptionsBuilder({
    translation: translations?.["category"],
    options: DEFAULT_CATEGORY_OPTIONS,
    adjustOptions: (options) => {
      return getFilteredCategoryOptions(
        options as CategoryOption[],
        assetClass2,
      );
    },
    deps: [assetClass2],
  });

  useAutoSelectSingleOption(
    category_options.options,
    form,
    isCategoryDisabled,
    translations,
    keysAllowNullValue,
  );

  return { category_options, isCategoryDisabled };
}
